import React, { useMemo } from "react";
import { ReactComponent as CrosspostIcon } from "assets/button-crosspost.svg";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { useUser } from "util/UserContext";
import type {
  EntityAvatarCrosspostData,
  EntityAvatarEntity,
  EntityAvatarEntityType,
} from "./EntityAvatar/entityAvatar.types";
import { mapEntityToAvatarViewModel } from "./EntityAvatar/mapEntityToAvatarViewModel";
import { useEntityAvatarData } from "./EntityAvatar/useEntityAvatarData";
import { EntityAvatarPresentation } from "./EntityAvatar/EntityAvatarPresentation";
import type { Action, Issue, WikiSuggestion } from "util/constants";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import { buildUserStoriesPath } from "components/Stories/paths";
import { useActorStoryStatus } from "components/Stories/useActorStoryStatus";

export interface EntityAvatarProps {
  entity?: EntityAvatarEntity;
  entityId?: number | string;
  entityType: EntityAvatarEntityType;
  isEntityProfilePage?: boolean;
  isEntitySection?: boolean;
  className?: string;
  entityChip?: boolean;
  isInputField?: boolean;
  showCloseChip?: boolean;
  badgeText?: string;
  onEntityChipClose?: () => void;
  showLink?: boolean;
  badgeElement?: React.ReactNode;
  isGeneralEntity?: boolean;
  hasRing?: boolean;
  customSubtitle?: string;
  compactCard?: boolean;
  displayMode?: "default" | "avatarOnly";
  crosspostData?: EntityAvatarCrosspostData;
}

/**
 * Entity identity row: avatar, labels, and links. Data loads via TanStack Query; mapping is pure.
 */
const EntityAvatar: React.FC<EntityAvatarProps> = ({
  entity,
  entityId,
  entityType,
  isEntityProfilePage = false,
  isEntitySection = false,
  className = "",
  entityChip = false,
  isInputField = true,
  showCloseChip = true,
  badgeText,
  onEntityChipClose,
  showLink = false,
  badgeElement,
  isGeneralEntity = false,
  hasRing = true,
  customSubtitle = "",
  compactCard = false,
  displayMode = "default",
  crosspostData,
}) => {
  // Trim any trailing slash so the downstream `${imageCdn}/${key}`
  // joins in `mapEntityToAvatarViewModel` always produce a single
  // separator, matching the canonical resolver in `actorIdentity.ts`.
  const IMAGE_CDN = String(process.env.REACT_APP_IMAGE_CDN ?? "").replace(/\/$/, "");
  const { currentUser } = useUser();
  const storyUi = getStoryUiStrings(resolveStoryLocale());

  const normalizedEntityId = useMemo(() => {
    const rawId =
      entityId ??
      (entity && typeof entity === "object" && "id" in entity
        ? (entity as Record<string, unknown>).id
        : undefined);
    if (typeof rawId === "number" && Number.isFinite(rawId) && rawId > 0) {
      return rawId;
    }
    if (typeof rawId === "string" && /^\d+$/.test(rawId)) {
      const parsed = Number.parseInt(rawId, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    }
    return undefined;
  }, [entityId, entity]);

  const {
    enrichedEntity,
    crosspostTargetEntity,
    wikipediaSummary,
    resolvedIssueThumbnail,
    resolvedThumbByKey,
    isLoading,
    isError,
  } = useEntityAvatarData({
    entity,
    entityId: normalizedEntityId,
    entityType,
    isInputField,
    crosspostData,
    currentUserId: currentUser?.id,
  });

  const details = useMemo(() => {
    const base = mapEntityToAvatarViewModel({
      entityType,
      entityData: enrichedEntity ?? undefined,
      imageCdn: IMAGE_CDN,
      isGeneralEntity,
      customSubtitle,
      crosspostData,
      crosspostTargetEntity:
        (crosspostData?.targetEntity as Record<string, unknown> | undefined) ?? crosspostTargetEntity,
      resolvedThumbByKey,
      resolvedIssueThumbnail,
      wikipediaSummary: wikipediaSummary as Record<string, unknown> | null,
    });

    if (
      entityType === "crosspost" &&
      crosspostData?.sourceType === "issue" &&
      crosspostData?.targetType === "issue"
    ) {
      return {
        ...base,
        separator: (
          <CrosspostIcon className="mx-1 size-3 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
        ),
      };
    }
    return base;
  }, [
    entityType,
    enrichedEntity,
    IMAGE_CDN,
    isGeneralEntity,
    customSubtitle,
    crosspostData,
    crosspostTargetEntity,
    resolvedThumbByKey,
    resolvedIssueThumbnail,
    wikipediaSummary,
  ]);

  const actorStoryUserId = useMemo(() => {
    if (
      !enrichedEntity ||
      typeof enrichedEntity !== "object" ||
      entityType === "user" ||
      entityType === "team" ||
      entityType === "feedback"
    ) {
      return null;
    }
    if (!("user_id" in enrichedEntity)) {
      return null;
    }
    const parsed = Number.parseInt(String((enrichedEntity as Record<string, unknown>).user_id), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [enrichedEntity, entityType]);

  // Derive actor story status via the canonical hook so the actor mini
  // avatar shares the same TanStack cache as `<ActorAvatar>` (no extra
  // network calls). The standardized hook also resolves `has_unseen`,
  // not just "any story exists" — but the chip surface only needs the
  // boolean today.
  const actorStoryStatus = useActorStoryStatus(
    actorStoryUserId
      ? {
          id: actorStoryUserId,
          kind: "user",
          username: "",
          displayName: null,
          avatarKey: null,
        }
      : null
  );
  const actorStoryHref = actorStoryUserId ? buildUserStoriesPath(actorStoryUserId) : null;

  const ringClass = hasRing
    ? "border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon"
    : "border border-transparent";

  const renderSkeleton = (): React.ReactElement => {
    if (displayMode === "avatarOnly") {
      return (
        <div className={`entity-avatar-only flex items-center justify-center ${className}`}>
          <LoadingSkeleton
            count={1}
            height="h-12"
            width="w-12"
            margin="m-0"
            rounded="rounded-full"
            containerClassName="flex"
          />
        </div>
      );
    }
    return (
      <div className={`entity-avatar-skeleton flex items-center gap-2 ${className}`}>
        <LoadingSkeleton
          count={1}
          height="h-9"
          width="w-9"
          margin="m-0"
          rounded="rounded-full"
          containerClassName="flex"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <LoadingSkeleton count={1} height="h-4" width="w-32" margin="m-0" rounded="rounded-full" containerClassName="flex" />
          <LoadingSkeleton count={1} height="h-3" width="w-24" margin="m-0" rounded="rounded-full" containerClassName="flex" />
        </div>
      </div>
    );
  };

  if (isLoading || !enrichedEntity) {
    if (isError) {
      return (
        <div className={`text-sm text-red-600 dark:text-red-400 ${className}`} role="alert">
          Unable to load entity.
        </div>
      );
    }
    return renderSkeleton();
  }

  return (
    <EntityAvatarPresentation
      details={details}
      entityType={entityType}
      entity={enrichedEntity as Issue | WikiSuggestion | Action | Record<string, unknown>}
      className={className}
      ringClass={ringClass}
      displayMode={displayMode}
      isEntityProfilePage={isEntityProfilePage}
      isEntitySection={isEntitySection}
      entityChip={entityChip}
      showLink={showLink}
      compactCard={compactCard}
      badgeText={badgeText}
      badgeElement={badgeElement}
      showCloseChip={showCloseChip}
      onEntityChipClose={onEntityChipClose}
      crosspostData={crosspostData}
      isGeneralEntity={isGeneralEntity}
      actorStoryHref={actorStoryHref}
      hasActorStory={actorStoryStatus.hasStory}
      hasActorUnseenStory={actorStoryStatus.hasUnseen}
      actorStoryLabel={storyUi.viewStory}
    />
  );
};

export default EntityAvatar;
